import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatMatchTime } from '../utils/time';

const liveMinuteStatuses = new Set(['1H', '2H', 'ET', 'LIVE', 'BT', 'INT']);

const getDisplayedLiveMinute = (match) => {
  const baseElapsed = Number(match?.status?.elapsed || 0);
  if (!baseElapsed) return 0;

  const statusShort = String(match?.status?.short || '');
  if (!liveMinuteStatuses.has(statusShort)) return baseElapsed;

  const sourceUpdatedAt = match?.sourceUpdatedAt ? new Date(match.sourceUpdatedAt).getTime() : 0;
  if (!sourceUpdatedAt) return baseElapsed;

  const elapsedSinceSyncMs = Date.now() - sourceUpdatedAt;
  if (elapsedSinceSyncMs <= 0) return baseElapsed;

  const extraMinutes = Math.min(2, Math.floor(elapsedSinceSyncMs / 60000));
  if (extraMinutes <= 0) return baseElapsed;

  const capByStatus = {
    '1H': 52,
    '2H': 105,
    ET: 125,
    LIVE: 125,
    BT: 125,
    INT: 105
  };

  const cap = capByStatus[statusShort] || 125;
  return Math.min(baseElapsed + extraMinutes, cap);
};

const hasGoalContinuationSignal = (liveInsights) =>
  Boolean(
    liveInsights?.insights?.some((item) =>
      ['Gol Devam Edebilir', 'Gol Devam Eder', 'Gol Olur', 'Guclu Gol Sinyali', 'Tempo Artiyor'].includes(item.label)
    )
  );

const hasNamedTeamSignal = (liveInsights) =>
  Boolean(liveInsights?.highlights?.recommendation?.selection);

const isNoPlayWarning = (liveInsights) =>
  liveInsights?.highlights?.recommendation?.status === 'none' ||
  String(liveInsights?.summary || '').startsWith('Oneri Yok');

const getNeutralOverLabel = (match, liveInsights) => {
  if (!match || !liveInsights || isNoPlayWarning(liveInsights)) return null;

  const hasGoalStyleSignal = Array.isArray(liveInsights?.insights) &&
    liveInsights.insights.some((item) =>
      ['Gol Devam Edebilir', 'Gol Devam Eder', 'Gol Olur', 'Gol Bekleniyor', 'Guclu Gol Sinyali', 'Tempo Artiyor'].includes(item?.label)
    );

  if (!hasGoalStyleSignal) return null;

  const totalGoals = Number(match?.goals?.home || 0) + Number(match?.goals?.away || 0);
  if (!Number.isFinite(totalGoals)) return null;

  return `${totalGoals + 0.5} Ust`;
};

const sanitizeLiveReason = (reason, hasTeamSignal) => {
  if (!reason) return null;
  if (String(reason).toLowerCase().includes('null')) {
    return hasTeamSignal
      ? 'Canli veri akisina gore bu taraf daha baskili gorunuyor.'
      : 'Canli veri akisinda sut, tempo ve baski sinyali izleniyor.';
  }
  return reason;
};

const formatMetricValue = (value, suffix = '') => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return `${numeric}${suffix}`;
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(String(value).replace('%', '').replace(',', '.').trim());
  return Number.isFinite(numeric) ? numeric : null;
};

const getValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const formatNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? String(numeric) : 'Veri bekleniyor';
};

const formatXg = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : 'Veri bekleniyor';
};

const formatPercentValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Veri bekleniyor';
  const stringValue = String(value);
  if (stringValue.includes('%')) return stringValue;
  const numeric = Number(stringValue);
  return Number.isFinite(numeric) ? `%${Math.round(numeric)}` : 'Veri bekleniyor';
};

const formatStatPair = (home, away) => {
  const homeValue = toFiniteNumber(home);
  const awayValue = toFiniteNumber(away);

  if (homeValue === null || awayValue === null) return 'Veri bekleniyor';
  return `${homeValue} - ${awayValue}`;
};

const formatPair = (home, away) => {
  const homeValue = toFiniteNumber(home);
  const awayValue = toFiniteNumber(away);

  if (homeValue === null || awayValue === null) return 'Veri bekleniyor';
  return `${homeValue} - ${awayValue}`;
};

const formatXgPair = (home, away) => {
  const homeValue = toFiniteNumber(home);
  const awayValue = toFiniteNumber(away);

  if (homeValue === null || awayValue === null) return 'Veri bekleniyor';
  return `${homeValue.toFixed(2)} - ${awayValue.toFixed(2)}`;
};

const formatPercentPair = (home, away) => {
  const homeValue = toFiniteNumber(home);
  const awayValue = toFiniteNumber(away);

  if (homeValue === null || awayValue === null) return 'Veri bekleniyor';
  return `${homeValue}% - ${awayValue}%`;
};

const parsePercentMetric = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(String(value).replace('%', '').replace(',', '.').trim());
  if (!Number.isFinite(numeric)) return null;
  return clampPercent(numeric, 0);
};

const getDecisionBadge = ({ liveInsights, noPlayWarning, riskNote }) => {
  const status = String(liveInsights?.highlights?.recommendation?.status || '').trim().toLowerCase();

  if (status === 'strong') {
    return { label: 'Guclu Gol Sinyali', tone: 'green' };
  }

  if (noPlayWarning) {
    return { label: 'Dengede', tone: 'gray' };
  }

  if (riskNote) {
    return { label: 'Risk Yuksek', tone: 'charcoal' };
  }

  return { label: 'Canli Takip', tone: 'gold' };
};

const buildDecisionDescription = ({ liveReason, liveInsights, noPlayWarning }) => {
  if (noPlayWarning) {
    return 'Son bolumde tempo dengeli ilerliyor. Net karar yerine mac akisinin izlenmesi daha saglikli gorunuyor.';
  }

  return liveReason || liveInsights?.summary || 'Son bolumde korner ve tempo artisi var. Gol sinyali izlenebilir seviyede.';
};

const buildSignalLabel = (item) => {
  const label = String(item?.label || '').trim();
  if (!label) return 'Canli Sinyal';
  if (label.includes('Korner')) return 'Korner Baskisi';
  if (label.includes('Gol')) return 'Gol Baskisi';
  if (label.includes('Tempo')) return 'Tempo Baskisi';
  return label;
};

const getTopSignals = (liveInsights) =>
  (Array.isArray(liveInsights?.insights) ? liveInsights.insights : [])
    .filter((item) => Number(item?.probability || 0) > 0)
    .sort((a, b) => Number(b?.probability || 0) - Number(a?.probability || 0))
    .slice(0, 2);

const getNumericMetric = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const resolveSignalTeamByStats = ({ match, liveInsights, signalType }) => {
  const homeName = String(match?.homeTeam?.name || '').trim();
  const awayName = String(match?.awayTeam?.name || '').trim();
  if (!homeName || !awayName) return null;

  const liveContext = liveInsights?.highlights?.liveContext || {};
  const liveXg = liveInsights?.highlights?.liveXg || {};
  const momentumLeader = String(liveInsights?.highlights?.momentum?.leader || '').trim();
  const dominantSide = String(liveInsights?.highlights?.dominantSide || '').trim();

  const homeCorners = getNumericMetric(liveContext?.corners?.home, 0);
  const awayCorners = getNumericMetric(liveContext?.corners?.away, 0);
  const homeDangerous = getNumericMetric(liveContext?.dangerousAttacks?.home, 0);
  const awayDangerous = getNumericMetric(liveContext?.dangerousAttacks?.away, 0);
  const homeShotsOnTarget = getNumericMetric(liveContext?.shotsOnTarget?.home, 0);
  const awayShotsOnTarget = getNumericMetric(liveContext?.shotsOnTarget?.away, 0);
  const homePossession = getNumericMetric(liveContext?.possession?.home, 0);
  const awayPossession = getNumericMetric(liveContext?.possession?.away, 0);
  const homeXg = getNumericMetric(liveXg?.home, 0);
  const awayXg = getNumericMetric(liveXg?.away, 0);

  if (signalType === 'Korner Baskisi') {
    const homeCornerScore = (homeCorners * 3) + (homeDangerous * 0.18) + (homePossession * 0.06);
    const awayCornerScore = (awayCorners * 3) + (awayDangerous * 0.18) + (awayPossession * 0.06);
    if (homeCornerScore !== awayCornerScore) {
      return homeCornerScore > awayCornerScore ? homeName : awayName;
    }
  }

  const homePressureScore =
    (homeXg * 22) +
    (homeShotsOnTarget * 6) +
    (homeDangerous * 0.16) +
    (homeCorners * 1.8) +
    (homePossession * 0.08);
  const awayPressureScore =
    (awayXg * 22) +
    (awayShotsOnTarget * 6) +
    (awayDangerous * 0.16) +
    (awayCorners * 1.8) +
    (awayPossession * 0.08);

  if (homePressureScore !== awayPressureScore) {
    return homePressureScore > awayPressureScore ? homeName : awayName;
  }

  if (momentumLeader === homeName || momentumLeader === awayName) return momentumLeader;
  if (dominantSide === homeName || dominantSide === awayName) return dominantSide;
  return null;
};

const resolveSignalTeamName = (match, liveInsights) => {
  const homeName = String(match?.homeTeam?.name || '').trim();
  const awayName = String(match?.awayTeam?.name || '').trim();
  const selection = String(liveInsights?.highlights?.recommendation?.selection || '').trim();
  const shortReason = String(liveInsights?.highlights?.shortReason || '').trim();
  const summary = String(liveInsights?.summary || '').trim();
  const leader = String(
    liveInsights?.highlights?.momentum?.leader ||
    liveInsights?.highlights?.dominantSide ||
    ''
  ).trim();

  const sources = [selection, shortReason, summary, leader];
  for (const source of sources) {
    if (!source) continue;
    if (homeName && source.includes(homeName)) return homeName;
    if (awayName && source.includes(awayName)) return awayName;
  }

  if (leader === homeName || leader === awayName) return leader;
  return null;
};

const buildSignalDirection = ({ match, liveInsights, topSignals, decisionBadge, hasGoalAlert, hasTeamSignal }) => {
  if (!liveInsights || ['Dengede', 'Risk Yuksek'].includes(decisionBadge.label)) {
    return { isNeutral: true, teamName: null, suffix: 'Dengede' };
  }

  const strongestSignalLabel = buildSignalLabel(topSignals[0]);
  const hasCornerSignal = topSignals.some((item) => buildSignalLabel(item) === 'Korner Baskisi');
  const hasGoalSignal =
    hasGoalAlert ||
    hasTeamSignal ||
    topSignals.some((item) => {
      const label = buildSignalLabel(item);
      return label === 'Gol Baskisi' || label === 'Tempo Baskisi';
    });

  if (hasCornerSignal) {
    const signalTeamName =
      resolveSignalTeamByStats({ match, liveInsights, signalType: strongestSignalLabel || 'Korner Baskisi' }) ||
      resolveSignalTeamName(match, liveInsights);
    if (!signalTeamName) {
      return { isNeutral: true, teamName: null, suffix: 'Dengede' };
    }
    return { isNeutral: false, teamName: signalTeamName, suffix: 'korner baskisi' };
  }

  if (hasGoalSignal) {
    const signalTeamName =
      resolveSignalTeamByStats({ match, liveInsights, signalType: strongestSignalLabel || 'Gol Baskisi' }) ||
      resolveSignalTeamName(match, liveInsights);
    if (!signalTeamName) {
      return { isNeutral: true, teamName: null, suffix: 'Dengede' };
    }
    return { isNeutral: false, teamName: signalTeamName, suffix: 'gol baskisi' };
  }

  return { isNeutral: true, teamName: null, suffix: 'Dengede' };
};

const isMarketSelection = (value) => {
  const text = String(value || '').trim();
  if (!text) return false;

  const normalized = text.toLowerCase();
  if (normalized.includes('baski') || normalized.includes('dengede') || normalized.includes('takip')) {
    return false;
  }

  return [
    'ust',
    'alt',
    'kg',
    'ms ',
    'ms1',
    'ms 1',
    'ms x',
    'ms2',
    'ms 2',
    'korner',
    '1.5',
    '2.5',
    '3.5',
    '4.5'
  ].some((keyword) => normalized.includes(keyword));
};

const getSignalMarketText = ({ liveRecommendationTitle, topSignals, strongestSignalLabel }) => {
  const strongestLabel = String(strongestSignalLabel || '').trim();
  const recommendationLooksLikeMarket = isMarketSelection(liveRecommendationTitle);
  const recommendationText = String(liveRecommendationTitle || '').trim();
  const recommendationLower = recommendationText.toLowerCase();

  if (strongestLabel === 'Korner Baskisi') {
    if (recommendationLooksLikeMarket && recommendationLower.includes('korner')) {
      return recommendationText;
    }

    const cornerMarket = topSignals.find((item) => {
      const label = String(item?.label || '').toLowerCase();
      return isMarketSelection(label) && label.includes('korner');
    })?.label;
    return cornerMarket || null;
  }

  if (isMarketSelection(liveRecommendationTitle)) {
    return liveRecommendationTitle;
  }

  const signalLabel = topSignals.find((item) => isMarketSelection(item?.label))?.label;
  return signalLabel || null;
};

const clampPercent = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(100, Math.round(numeric)));
};

const countryCodeToFlagEmoji = (code) => {
  const normalized = String(code || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return '🌍';
  return String.fromCodePoint(...normalized.split('').map((char) => 127397 + char.charCodeAt(0)));
};

const MatchCard = ({ match, isLive, onAddToCoupon, isInCoupon, isFavorite = false, onToggleFavorite, liveLayout = 'default' }) => {
  const { isPremium, isAdmin } = useAuth();
  const [showDetails, setShowDetails] = useState(false);
  const matchTime = formatMatchTime(match.date);
  const displayedLiveMinute = getDisplayedLiveMinute(match);

  const popularityScore = Math.round(match.predictionContext?.popularityScore || match.popularityScore || 0);
  const canSeeLiveAnalysis = Boolean(match?.liveAnalysisAccessible || isAdmin);
  const liveInsights = isLive && canSeeLiveAnalysis ? match.liveInsights : null;
  const liveRecommendation = liveInsights?.highlights?.recommendation || null;
  const liveContext = liveInsights?.highlights?.liveContext || null;
  const noPlayWarning = isNoPlayWarning(liveInsights);
  const hasGoalAlert = hasGoalContinuationSignal(liveInsights);
  const hasTeamSignal = hasNamedTeamSignal(liveInsights);
  const neutralOverLabel = !hasTeamSignal && !noPlayWarning ? getNeutralOverLabel(match, liveInsights) : null;
  const liveReason = sanitizeLiveReason(liveInsights?.highlights?.shortReason, hasTeamSignal);
  const liveMomentum = liveInsights?.highlights?.momentum;
  const cardImpact = liveInsights?.highlights?.cardImpact;
  const oddsSupport = liveInsights?.highlights?.oddsSupport || null;
  const selectedOdd = match.oddsAnalysis?.selectedOdd ?? oddsSupport?.liveOdds ?? oddsSupport?.preMatchOdds ?? null;
  const hasOddsSupport = Number.isFinite(Number(selectedOdd)) && Number(selectedOdd) > 1;
  const liveEdge = hasOddsSupport ? liveRecommendation?.liveEdge : null;
  const impliedProbability = hasOddsSupport
    ? (liveRecommendation?.impliedProbability ?? oddsSupport?.impliedProbability ?? null)
    : null;
  const riskNote = liveInsights?.highlights?.riskNote || null;
  const liveRecommendationTitle = liveRecommendation?.selection || neutralOverLabel || null;
  const decisionBadge = getDecisionBadge({ liveInsights, noPlayWarning, riskNote });
  const decisionDescription = buildDecisionDescription({ liveReason, liveInsights, noPlayWarning });
  const topSignals = getTopSignals(liveInsights);
  const signalDirection = buildSignalDirection({
    match,
    liveInsights,
    topSignals,
    decisionBadge,
    hasGoalAlert,
    hasTeamSignal
  });
  const showPremiumLiveCard = Boolean(isLive && liveInsights);

  const minute = displayedLiveMinute || match.minute || match.fixture?.status?.elapsed || '-';
  const minuteLabel = minute === '-' ? '-' : `${minute}'`;
  const leagueName = match.league?.name || match.league || match.leagueName || '-';
  const leagueRound = match.league?.round || match.round || match.stage || 'Regular Season';
  const homeTeamName = match.homeTeam?.name || match.home?.name || match.homeTeam || '-';
  const awayTeamName = match.awayTeam?.name || match.away?.name || match.awayTeam || '-';
  const homeLogo = match.homeTeam?.logo || match.home?.logo || '';
  const awayLogo = match.awayTeam?.logo || match.away?.logo || '';
  const homeScore = match.goals?.home ?? match.homeScore ?? match.goals?.home ?? 0;
  const awayScore = match.goals?.away ?? match.awayScore ?? match.goals?.away ?? 0;
  const decisionLabel = decisionBadge.label || match.decisionLabel || match.recommendation || 'Dengede';
  const shortReason =
    decisionDescription ||
    match.shortReason ||
    match.reason ||
    'Son bolumde tempo dengeli ilerliyor.';
  const strongestSignalText = topSignals.length
    ? `${buildSignalLabel(topSignals[0])} %${Number(topSignals[0]?.probability || 0)}`
    : 'Sinyal akisi dengeli';
  const strongestSignalLabel = topSignals.length ? buildSignalLabel(topSignals[0]) : null;
  const hasGoalSignal =
    decisionLabel === 'Guclu Gol Sinyali' ||
    String(strongestSignalText || '').toLowerCase().includes('gol');
  const decisionClassMap = {
    green: 'decision-strong',
    gold: 'decision-follow',
    gray: 'decision-neutral',
    charcoal: 'decision-risk'
  };
  const decisionClass = decisionClassMap[decisionBadge.tone] || 'decision-neutral';
  const signalDirectionText = signalDirection.isNeutral
    ? (match.signalDirection || 'Dengede')
    : `${signalDirection.teamName} ${signalDirection.suffix}`;
  const signalMarketText = getSignalMarketText({ liveRecommendationTitle, topSignals, strongestSignalLabel });
  const momentumScore = clampPercent(liveMomentum?.score ?? match.momentum, 0);
  const confidenceScore = clampPercent(liveRecommendation?.confidence ?? match.confidence, 0);
  const homeStats = match.liveStatistics?.home || match.statistics?.home || {};
  const awayStats = match.liveStatistics?.away || match.statistics?.away || {};
  const homeLiveXg = getValue(
    match.liveStats?.homeLiveXg,
    match.liveStats?.home?.xg,
    match.liveStatistics?.home?.liveXg,
    match.statistics?.home?.liveXg,
    match.homeLiveXg
  );
  const awayLiveXg = getValue(
    match.liveStats?.awayLiveXg,
    match.liveStats?.away?.xg,
    match.liveStatistics?.away?.liveXg,
    match.statistics?.away?.liveXg,
    match.awayLiveXg
  );
  const homeShotsOnGoal = getValue(
    match.liveStats?.homeShotsOnGoal,
    match.liveStatistics?.home?.shotsOnGoal,
    match.statistics?.home?.shotsOnGoal,
    match.homeShotsOnGoal
  );
  const awayShotsOnGoal = getValue(
    match.liveStats?.awayShotsOnGoal,
    match.liveStatistics?.away?.shotsOnGoal,
    match.statistics?.away?.shotsOnGoal,
    match.awayShotsOnGoal
  );
  const homeTotalShots = getValue(
    match.liveStats?.homeTotalShots,
    match.liveStatistics?.home?.totalShots,
    match.statistics?.home?.totalShots,
    match.homeTotalShots
  );
  const awayTotalShots = getValue(
    match.liveStats?.awayTotalShots,
    match.liveStatistics?.away?.totalShots,
    match.statistics?.away?.totalShots,
    match.awayTotalShots
  );
  const homeCorners = getValue(
    match.liveStats?.homeCorners,
    match.liveStatistics?.home?.cornerKicks,
    match.statistics?.home?.cornerKicks,
    match.statistics?.home?.corners,
    match.homeCorners
  );
  const awayCorners = getValue(
    match.liveStats?.awayCorners,
    match.liveStatistics?.away?.cornerKicks,
    match.statistics?.away?.cornerKicks,
    match.statistics?.away?.corners,
    match.awayCorners
  );
  const homePossession = getValue(
    match.liveStats?.homePossession,
    match.liveStatistics?.home?.possession,
    match.liveStatistics?.home?.ballPossession,
    match.statistics?.home?.possession,
    match.statistics?.home?.ballPossession,
    match.homePossession
  );
  const awayPossession = getValue(
    match.liveStats?.awayPossession,
    match.liveStatistics?.away?.possession,
    match.liveStatistics?.away?.ballPossession,
    match.statistics?.away?.possession,
    match.statistics?.away?.ballPossession,
    match.awayPossession
  );
  const homeXgValue = toFiniteNumber(homeStats.liveXg);
  const awayXgValue = toFiniteNumber(awayStats.liveXg);
  const homeCornersValue = toFiniteNumber(homeStats.cornerKicks ?? homeStats.corners);
  const awayCornersValue = toFiniteNumber(awayStats.cornerKicks ?? awayStats.corners);
  const homeShotsOnGoalValue = toFiniteNumber(homeStats.shotsOnGoal);
  const awayShotsOnGoalValue = toFiniteNumber(awayStats.shotsOnGoal);
  const homeTotalShotsValue = toFiniteNumber(homeStats.totalShots);
  const awayTotalShotsValue = toFiniteNumber(awayStats.totalShots);
  const rawHomePossession = parsePercentMetric(homeStats.ballPossession ?? homeStats.possession);
  const rawAwayPossession = parsePercentMetric(awayStats.ballPossession ?? awayStats.possession);
  const hasXgPair = homeXgValue !== null && awayXgValue !== null;
  const hasCornerPair = homeCornersValue !== null && awayCornersValue !== null;
  const hasPossessionPair = rawHomePossession !== null && rawAwayPossession !== null;
  const totalXg = hasXgPair ? Math.max(0, homeXgValue) + Math.max(0, awayXgValue) : 0;
  const totalCorners = hasCornerPair ? Math.max(0, homeCornersValue) + Math.max(0, awayCornersValue) : 0;
  const homePossessionValue = hasPossessionPair ? rawHomePossession : null;
  const awayPossessionValue = hasPossessionPair ? rawAwayPossession : null;
  const xgBarWidth = hasXgPair && totalXg > 0
    ? clampPercent((Math.max(homeXgValue, awayXgValue) / totalXg) * 100, 0)
    : 0;
  const cornerBarWidth = hasCornerPair && totalCorners > 0
    ? clampPercent((Math.max(homeCornersValue, awayCornersValue) / totalCorners) * 100, 0)
    : 0;
  const possessionBarWidth = hasPossessionPair
    ? clampPercent(Math.max(homePossessionValue, awayPossessionValue), 0)
    : 0;
  const popularity = Number.isFinite(popularityScore) ? popularityScore : 0;
  const flagEmoji = countryCodeToFlagEmoji(match.league?.countryCode || match.league?.code || match.league?.countryShort);
  const liveStatusLabel = 'CANLI';
  const signalStatus = decisionLabel || 'Canli Takip';
  const signalTitle = signalMarketText || strongestSignalText || signalDirectionText || 'Mac akisinda sinyal izleniyor';
  const signalText = canSeeLiveAnalysis
    ? shortReason
    : 'Canli veri akisi izleniyor. Detayli AI yorum uygun oldugunda kart icine yansir.';
  const stageText = leagueRound || 'Canli mac';

  const providerPressureLabel = getValue(
    match.liveStats?.pressureLabel,
    match.liveStats?.last10Pressure?.label,
    match.liveStatistics?.last10Pressure?.label,
    match.statistics?.last10Pressure?.label,
    liveInsights?.highlights?.pressure?.label
  );
  const providerPressureText = getValue(
    match.liveStats?.pressureText,
    match.liveStats?.last10Pressure?.text,
    match.liveStatistics?.last10Pressure?.text,
    match.statistics?.last10Pressure?.text,
    liveInsights?.highlights?.pressure?.text
  );
  const homePressureScore =
    Number(homeShotsOnGoal || 0) * 3 +
    Number(homeTotalShots || 0) * 1.2 +
    Number(homeCorners || 0) * 1.4 +
    Number(homeLiveXg || 0) * 8;
  const awayPressureScore =
    Number(awayShotsOnGoal || 0) * 3 +
    Number(awayTotalShots || 0) * 1.2 +
    Number(awayCorners || 0) * 1.4 +
    Number(awayLiveXg || 0) * 8;
  const hasPressureMetrics = [
    homeShotsOnGoal,
    awayShotsOnGoal,
    homeTotalShots,
    awayTotalShots,
    homeCorners,
    awayCorners,
    homeLiveXg,
    awayLiveXg
  ].some((value) => toFiniteNumber(value) !== null);

  let pressureLabel = 'Dengede';
  let pressureText = 'Son bolumde baski dengeli ilerliyor.';

  if (providerPressureLabel) {
    pressureLabel = providerPressureLabel;
    pressureText = providerPressureText || 'Canli servis son bolum baskisini bu sekilde raporluyor.';
  } else if (!hasPressureMetrics) {
    pressureLabel = 'Veri bekleniyor';
    pressureText = 'Son 10 dakika baski verisi henuz olusmadi.';
  } else if (homePressureScore >= awayPressureScore + 6) {
    pressureLabel = `${homeTeamName} gol baskisi`;
    pressureText = `${homeTeamName} son bolumde daha fazla pozisyon ve baski uretiyor.`;
  } else if (awayPressureScore >= homePressureScore + 6) {
    pressureLabel = `${awayTeamName} gol baskisi`;
    pressureText = `${awayTeamName} son bolumde daha fazla pozisyon ve baski uretiyor.`;
  }

  const liveDetailsPanel = showDetails ? (
    <div className="live-match-details-panel">
      <div className="live-detail-row">
        <span>Canli xG</span>
        <strong>{formatXg(homeLiveXg)} / {formatXg(awayLiveXg)}</strong>
      </div>
      <div className="live-detail-row">
        <span>Isabetli Sut</span>
        <strong>{formatNumber(homeShotsOnGoal)} / {formatNumber(awayShotsOnGoal)}</strong>
      </div>
      <div className="live-detail-row">
        <span>Toplam Sut</span>
        <strong>{formatNumber(homeTotalShots)} / {formatNumber(awayTotalShots)}</strong>
      </div>
      <div className="live-detail-row">
        <span>Korner</span>
        <strong>{formatNumber(homeCorners)} / {formatNumber(awayCorners)}</strong>
      </div>
      <div className="live-detail-row">
        <span>Topa Sahip Olma</span>
        <strong>{formatPercentValue(homePossession)} / {formatPercentValue(awayPossession)}</strong>
      </div>
      <div className="live-pressure-box">
        <div>
          <span>Son 10 Dakika Gol Baskisi</span>
          <strong>{pressureLabel}</strong>
        </div>
        <p>{pressureText}</p>
      </div>
    </div>
  ) : null;
  const detailRows = [
    { label: 'Canli xG', value: formatXgPair(homeLiveXg, awayLiveXg) },
    { label: 'Isabetli Sut', value: formatPair(homeShotsOnGoal, awayShotsOnGoal) },
    { label: 'Toplam Sut', value: formatPair(homeTotalShots, awayTotalShots) },
    { label: 'Korner', value: formatPair(homeCorners, awayCorners) },
    { label: 'Topla Oynama', value: formatPair(homePossession, awayPossession) },
    { label: 'Son 10 Dakika Gol Baskisi', value: pressureLabel }
  ];

  if (isLive && liveLayout === 'home-app') {
    return (
      <article className={`live-app-card live-analysis-isolated-card ${isLive ? 'is-live' : ''} ${hasGoalSignal ? 'has-goal-signal' : ''}`}>
        <div className="live-app-header">
          <div className="live-league-pill">{leagueName}</div>
          <div className="live-minute-pill">{minuteLabel}</div>
        </div>

        <div className="live-stage-text">{stageText}</div>

        <div className="live-scoreboard">
          <div className="live-team-line">
            {homeLogo ? <img className="live-team-logo" src={homeLogo} alt={homeTeamName} loading="lazy" /> : <div className="live-team-logo" aria-hidden="true" />}
            <div className="live-team-name-full" title={homeTeamName}>{homeTeamName}</div>
            <div className="live-team-score">{homeScore}</div>
          </div>

          <div className="live-team-line">
            {awayLogo ? <img className="live-team-logo" src={awayLogo} alt={awayTeamName} loading="lazy" /> : <div className="live-team-logo" aria-hidden="true" />}
            <div className="live-team-name-full" title={awayTeamName}>{awayTeamName}</div>
            <div className="live-team-score">{awayScore}</div>
          </div>
        </div>

        <div className="live-ai-signal-panel">
          <div className="live-ai-signal-top">
            <span className="live-ai-minute">{minuteLabel}</span>
            <div className="live-ai-top-right">
              {confidenceScore > 0 ? <span className="live-ai-trust">%{confidenceScore}</span> : null}
              <span className="live-ai-status">{signalStatus}</span>
            </div>
          </div>
          <div className="live-ai-title">{signalTitle}</div>
          <div className="live-ai-text">{signalText}</div>
        </div>

        <button
          type="button"
          className="live-details-toggle"
          onClick={() => setShowDetails((value) => !value)}
        >
          {showDetails ? 'Detayları Gizle' : 'Detayları Aç'}
        </button>

        {showDetails ? (
          <div className="live-details-panel">
            <div className="live-stat-row">
              <span>Canlı xG</span>
              <strong>{formatXgPair(homeLiveXg, awayLiveXg)}</strong>
            </div>
            <div className="live-stat-row">
              <span>İsabetli Şut</span>
              <strong>{formatPair(homeShotsOnGoal, awayShotsOnGoal)}</strong>
            </div>
            <div className="live-stat-row">
              <span>Toplam Şut</span>
              <strong>{formatPair(homeTotalShots, awayTotalShots)}</strong>
            </div>
            <div className="live-stat-row">
              <span>Korner</span>
              <strong>{formatPair(homeCorners, awayCorners)}</strong>
            </div>
            <div className="live-stat-row">
              <span>Topla Oynama</span>
              <strong>{formatPair(homePossession, awayPossession)}</strong>
            </div>
          </div>
        ) : null}

        <div className="live-disclaimer">
          Bunlar sadece analizdir. Oynamak ya da oynamamak sizin kararınız.
        </div>
      </article>
    );
  }

  return (
    <div className={`match-card ${isLive ? 'live live-premium-dashboard' : ''} ${showPremiumLiveCard ? 'live-card-shell live-match-card live-analysis-card-shell' : ''} ${showPremiumLiveCard && hasGoalSignal ? 'live-goal-priority' : ''}`}>
      {showPremiumLiveCard ? (
        <div className={`live-premium-card live-match-card live-analysis-isolated-card ${hasGoalSignal ? 'goal-signal-active' : ''} ${momentumScore >= 82 ? 'high-momentum-card' : ''} ${confidenceScore >= 75 ? 'high-confidence-card' : ''}`}>
          <div className="live-terminal-ribbon">
            <span className="live-terminal-ribbon-dot" />
            <strong>{hasGoalSignal ? 'PRIORITY SIGNAL' : momentumScore >= 82 ? 'HIGH MOMENTUM' : 'LIVE TERMINAL'}</strong>
            <small>{momentumScore}/100 momentum</small>
          </div>
          {hasGoalSignal ? (
            <div className="goal-signal-alert">
              <span className="signal-dot" />
              GOL SINYALI GELDI
            </div>
          ) : null}

          <div className="live-match-top">
            <div className="league-pill">
              <span>{flagEmoji}</span>
              <span>{leagueName}</span>
            </div>

            <div className="top-actions">
              {onToggleFavorite ? (
                <button
                  type="button"
                  className={`favorite-star ${isFavorite ? 'active' : ''}`}
                  onClick={() => onToggleFavorite(match)}
                  aria-label={isFavorite ? 'Takipten cikar' : 'Takibe ekle'}
                  title={isFavorite ? 'Takipten cikar' : 'Yildiza bas, bu mac derin canli analize girsin'}
                >
                  {isFavorite ? '\u2605' : '\u2606'}
                </button>
              ) : null}
              <div className="minute-pill">{minuteLabel}</div>
              <div className="status-pill">{liveStatusLabel}</div>
            </div>
          </div>

          <div className="match-meta-row">
            <span>{leagueRound}</span>
            <span className="popularity-pill">Populerlik {popularity}</span>
            <span className="live-open-pill">Canli analiz acik</span>
          </div>

          <div className="scoreboard-row live-team-row">
            <div className="team-side home-team" style={{ minWidth: 0, flex: 1 }}>
              {homeLogo ? <img className="team-logo" src={homeLogo} alt={homeTeamName} loading="lazy" /> : null}
              <strong
                className="live-team-name"
                style={{
                  fontSize: '12px',
                  lineHeight: '15px',
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'unset',
                  wordBreak: 'break-word'
                }}
              >
                {homeTeamName}
              </strong>
            </div>

            <div className="score-box live-score-box">{homeScore} : {awayScore}</div>

            <div className="team-side away-team" style={{ minWidth: 0, flex: 1 }}>
              <strong
                className="live-team-name"
                style={{
                  fontSize: '12px',
                  lineHeight: '15px',
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'unset',
                  wordBreak: 'break-word'
                }}
              >
                {awayTeamName}
              </strong>
              {awayLogo ? <img className="team-logo" src={awayLogo} alt={awayTeamName} loading="lazy" /> : null}
            </div>
          </div>

          <div className="live-match-card-body">
            <div className={`analysis-panel live-match-summary ${decisionClass}`}>
            <div className="analysis-panel-head">
              <div>
                <span className="minute-mini">{minuteLabel}</span>
                <span>{leagueName}</span>
              </div>
              <span className={`decision-badge ${decisionClass}`}>{decisionLabel}</span>
            </div>

            <p className="live-card-description">{shortReason}</p>

            <div className="divider" />

            <div className="signal-direction">
              <div className="section-label">SINYAL YONU</div>
              <div className="signal-value">
                <span className="signal-icon">{signalDirection.isNeutral ? '⚖' : '◎'}</span>
                {signalDirection.isNeutral ? (
                  <strong>{signalDirectionText}</strong>
                ) : (
                  <strong>
                    <span className="signal-team">{signalDirection.teamName}</span> {signalDirection.suffix}
                  </strong>
                )}
              </div>
              {signalMarketText ? (
                <div className="signal-market-chip">
                  <span>Onerilen Market</span>
                  <strong>{signalMarketText}</strong>
                </div>
              ) : null}
            </div>

            <div className="metric-grid live-stats-grid">
              <div className="metric-card live-stat-box">
                <div className="metric-card-head">
                  <span className="metric-icon">⚡</span>
                  <span>Momentum</span>
                </div>
                <b>{momentumScore}/100</b>
                <div className="metric-bar"><i style={{ width: `${momentumScore}%` }} /></div>
              </div>

              <div className="metric-card live-stat-box">
                <div className="metric-card-head">
                  <span className="metric-icon">✓</span>
                  <span>Guven</span>
                </div>
                <b>{confidenceScore}%</b>
                <div className="metric-bar green"><i style={{ width: `${confidenceScore}%` }} /></div>
              </div>

              <div className="metric-card live-stat-box">
                <div className="metric-card-head">
                  <span className="metric-icon">◔</span>
                  <span>Tahmini xG</span>
                </div>
                <b>{formatXgPair(homeXgValue, awayXgValue)}</b>
                <div className="metric-bar blue"><i style={{ width: `${xgBarWidth}%` }} /></div>
              </div>

              <div className="metric-card live-stat-box desktop-only">
                <div className="metric-card-head">
                  <span className="metric-icon">⌜</span>
                  <span>Korner</span>
                </div>
                <b>{formatStatPair(homeCornersValue, awayCornersValue)}</b>
                <div className="metric-bar purple"><i style={{ width: `${cornerBarWidth}%` }} /></div>
              </div>

              <div className="metric-card live-stat-box desktop-only">
                <div className="metric-card-head">
                  <span className="metric-icon">◫</span>
                  <span>Topla Oyn.</span>
                </div>
                <b>{formatPercentPair(homePossessionValue, awayPossessionValue)}</b>
                <div className="metric-bar teal"><i style={{ width: `${possessionBarWidth}%` }} /></div>
              </div>
            </div>

            <div className="strongest-signal">
              <div className="section-label">EN GUCLU SINYAL</div>
              <div className="signal-chip">
                <span>◎</span>
                <strong>{strongestSignalText}</strong>
              </div>
            </div>

            <div className="live-pressure-timeline">
              <div className="live-pressure-node is-active">
                <span>{minuteLabel}</span>
                <strong>Canlı akış</strong>
              </div>
              <div className={`live-pressure-node ${momentumScore >= 70 ? 'is-active' : ''}`}>
                <span>{momentumScore}</span>
                <strong>Momentum</strong>
              </div>
              <div className={`live-pressure-node ${confidenceScore >= 70 ? 'is-active' : ''}`}>
                <span>%{confidenceScore}</span>
                <strong>Güven</strong>
              </div>
            </div>

            <details className="live-details-accordion live-match-details">
              <summary className="details-button live-action-button">
                Detaylari Ac
                <span className="details-button-arrow">›</span>
              </summary>
              <div className="live-detail-list">
                {detailRows.map((item) => (
                  <div className="live-detail-row" key={`${match.fixtureId || match.id}-${item.label}`}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </details>
            </div>
          </div>

          <div className="live-disclaimer">
            <span>🛡</span>
            <strong>BUNLAR SADECE ANALIZDIR.<br />OYNAMAK YA DA OYNAMAMAK SIZIN KARARINIZ.</strong>
          </div>
        </div>
      ) : (
        <>
          <div className="match-header">
            <span className="league">{match.league?.country} - {match.league?.name}</span>
            <div className="match-header-actions">
              {isLive && onToggleFavorite ? (
                <button
                  type="button"
                  className={`favorite-star ${isFavorite ? 'active' : ''}`}
                  onClick={() => onToggleFavorite(match)}
                  aria-label={isFavorite ? 'Takipten cikar' : 'Takibe ekle'}
                  title={isFavorite ? 'Takipten cikar' : 'Yildiza bas, bu mac derin canli analize girsin'}
                >
                  {isFavorite ? '\u2605' : '\u2606'}
                </button>
              ) : null}
              <span className={`status ${isLive ? 'live-badge' : ''}`}>
                {isLive ? `${displayedLiveMinute}'` : matchTime}
              </span>
            </div>
          </div>

          <div className="match-subheader">
            <span>{match.league?.round || 'Ana Fikstur'}</span>
            <div className="match-subheader-meta">
              {popularityScore > 0 ? <span>Populerlik {popularityScore}</span> : null}
              {isLive ? <span className="premium-inline-tag">{canSeeLiveAnalysis ? 'Canli analiz acik' : 'Canli analiz kilitli'}</span> : null}
            </div>
          </div>

          <div className="match-teams">
            <div className="team home">
              <img src={match.homeTeam?.logo} alt={match.homeTeam?.name} loading="lazy" />
              <span>{match.homeTeam?.name}</span>
            </div>

            <div className="score">
              {isLive ? (
                <>
                  <span>{match.goals?.home}</span>
                  <span className="separator">:</span>
                  <span>{match.goals?.away}</span>
                </>
              ) : (
                <span className="vs">VS</span>
              )}
            </div>

            <div className="team away">
              <span>{match.awayTeam?.name}</span>
              <img src={match.awayTeam?.logo} alt={match.awayTeam?.name} loading="lazy" />
            </div>
          </div>

          <div className="match-footer">
            <span className={isLive ? 'live-analysis-disclaimer live-analysis-disclaimer-soft live-disclaimer' : ''}>
              {isLive
                ? 'BUNLAR SADECE ANALIZDIR. OYNAMAK YA DA OYNAMAMAK SIZIN KARARINIZ.'
                : (match.league?.round || 'Mac merkezi')}
            </span>
            {!isLive ? <span>{match.status?.long || 'Planlandi'}</span> : null}
          </div>
        </>
      )}

      {isLive && !canSeeLiveAnalysis && !isPremium && !isAdmin ? (
        <div className="premium-live-lock premium-live-lock-blink">
          ANALIZ ICIN LUTFEN PREMIUM UYE OLUNUZ
        </div>
      ) : null}

      {onAddToCoupon ? (
        <button
          className={`add-btn ${isInCoupon ? 'added' : ''}`}
          onClick={() => onAddToCoupon(match)}
        >
          {isInCoupon ? 'Kuponda' : 'Kupona Ekle'}
        </button>
      ) : null}
    </div>
  );
};


export default MatchCard;
